import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { eventsAPI, interactionsAPI, hubError } from '../lib/api';
import { useLiveEvents } from '../context/LiveEventsContext';
import { useInteractionTracker, useItemTimeTracker } from '../hooks/useInteractionTracker';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '../components/ui/tooltip';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from '../components/ui/dialog';
import { CalendarDays, Plus, Edit, Trash2, Loader2, MapPin, Clock, Eye, MousePointer, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../components/ui/alert-dialog';

const LIVE_EVENT_EVENTS = ['event.created'];

export default function EventsPage() {
  const { selectedChurchId, canCreate, canUpdate, canDelete, canRead } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', date: '', time: '', location: '' });
  const [saving, setSaving] = useState(false);
  const [badges, setBadges] = useState({});
  const [total, setTotal] = useState(0);
  const pg = usePagination(String(selectedChurchId));

  // Interaction tracking
  const { trackClick } = useInteractionTracker('event', null, null, selectedChurchId, { trackPageView: false, trackTimeSpent: false });
  useItemTimeTracker('event', editing, form.title, selectedChurchId, dialogOpen);

  const fetchEvents = () => {
    setLoading(true);
    eventsAPI.list(undefined, pg.params)
      .then(res => {
        const data = res.data?.data || [];
        setEvents(data);
        setTotal(res.data?.total || 0);
        // Fetch interaction badges
        if (data.length > 0 && canRead('analytics')) {
          const ids = data.map(e => e.id);
          interactionsAPI.badges('event', ids, selectedChurchId)
            .then(bRes => setBadges(bRes.data?.data || {}))
            .catch(() => {});
        }
      })
      .catch(() => toast.error('Error al cargar eventos'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchEvents(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedChurchId, pg.params]);

  useLiveEvents(LIVE_EVENT_EVENTS, (events) => {
    toast.info(`Nuevo evento publicado: ${events[events.length - 1].payload?.title || ''}`);
    fetchEvents();
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', description: '', date: '', time: '', location: '' });
    setDialogOpen(true);
  };

  const openEdit = (event) => {
    trackClick(event.id, event.title);
    setEditing(event.id);
    setForm({
      title: event.title || '',
      description: event.description || '',
      date: event.date || '',
      time: event.time ? String(event.time).slice(0, 5) : '',
      location: event.location || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title) { toast.error('El título es requerido'); return; }
    setSaving(true);
    try {
      if (editing) {
        await eventsAPI.update(editing, form);
        toast.success('Evento actualizado');
      } else {
        await eventsAPI.create(form);
        toast.success('Evento creado');
      }
      setDialogOpen(false);
      fetchEvents();
    } catch (err) {
      toast.error(hubError(err, 'Error al guardar evento'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await eventsAPI.delete(id);
      toast.success('Evento eliminado');
      fetchEvents();
    } catch (err) {
      toast.error(hubError(err, 'Error al eliminar evento'));
    }
  };

  return (
    <div>
      <TopBar
        title="Eventos"
        onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo}
        actionButton={
          <Button className="bg-[#056A84] hover:bg-[#045A70] text-white btn-lift" onClick={openCreate} data-testid="events-create-button">
            <Plus className="w-4 h-4 mr-1" /> Nuevo
          </Button>
        }
      />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        <Pagination
          total={total} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          label="eventos" testId="events-pagination"
        />
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 spinner text-[#056A84]" />
          </div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            <CalendarDays className="w-12 h-12 text-muted-foreground/40" />
            <p>No hay eventos registrados</p>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white mt-2" onClick={openCreate}>Crear primer evento</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((event) => {
              const attrs = event;
              return (
                <Card key={event.id} className="stat-card">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#1F9D7A]/10 flex items-center justify-center">
                        <CalendarDays className="w-5 h-5 text-[#1F9D7A]" />
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(event)} data-testid={`event-edit-${event.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`event-delete-${event.id}`}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar evento?</AlertDialogTitle>
                              <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(event.id)} className="bg-destructive text-destructive-foreground">Eliminar</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">{attrs.title}</h3>
                    {attrs.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{attrs.description}</p>}
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                      {attrs.date && (
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" />
                          {new Date(attrs.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                      {attrs.time && (
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{attrs.time}</span>
                      )}
                      {attrs.location && (
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{attrs.location}</span>
                      )}
                    </div>
                    {/* Interaction Badges */}
                    {badges[String(event.id)] && (
                      <TooltipProvider>
                        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border/50" data-testid={`event-badges-${event.id}`}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Eye className="w-3 h-3 text-[#056A84]" />
                                <span className="font-medium">{badges[String(event.id)].views}</span>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent><p>Vistas totales</p></TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <MousePointer className="w-3 h-3 text-[#1F9D7A]" />
                                <span className="font-medium">{badges[String(event.id)].clicks}</span>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent><p>Clics</p></TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Users className="w-3 h-3 text-[#2B7DBA]" />
                                <span className="font-medium">{badges[String(event.id)].unique_users}</span>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent><p>Usuarios únicos</p></TooltipContent>
                          </Tooltip>
                        </div>
                      </TooltipProvider>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Evento' : 'Nuevo Evento'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="event-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} data-testid="event-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="event-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Hora</Label>
                <Input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} data-testid="event-time-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Ubicación</Label>
              <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} data-testid="event-location-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={handleSave} disabled={saving} data-testid="event-save-button">
              {saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : null}
              {editing ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
